import {
  Button, Loading, Table, Icon, Tooltip, Toast,
} from '@roo/roo';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Column } from '@roo/roo/Table';
import { useQuery } from '@hooks/query';
import { numberMap } from '@utils/utils';
import { getJudgeProjects, batchSubmit } from '@assets/apis/review';
import Header from '@components/HeaderWithoutMenu';
import VoteModal from '@components/Review/VoteModal';
import Empty from './Empty';

import './index.scss';

const judgeStatusMap = ['toReview', 'reviewing', 'submitted'];

const mockJudgeProjects = {
  msg: 'ok',
  code: 0,
  data:
  {
    total: 2,
    groups: [
      {
        groupId: 46,
        groupName: '化学史-14000',
        projectConfigName: '青山科技奖项目',
        projectStageName: '二审',
        groupStatus: '已确认',
        rounds: [
          {
            id: 1,
            needReview: true,
            needVote: true,
            voteStatus: 0,
            projects: [
              {
                id: 123,
                projectNo: 'YSP2021071910072',
                judgeStatus: 2,
                judgeStatusName: '已提交(代评审)',
                score: 85,
                reportDirectionName: null,
                scientistName: '美小团',
                organizationName: null,
              },
            ],
          },
        ],
      },
    ],
  },
};

enum VoteStatus {
  DISABLED = 0,
  AVAILABLE,
  DONE,
}

const getJudgeApproach = (needReview, needVote) => {
  if (needReview && needVote) {
    return '评审+投票';
  } else if (needReview) {
    return '评审';
  } else if (needVote) {
    return '投票';
  } else {
    return '-';
  }
};
const infoRenderTh = (column) => {
  const { label } = column;
  return (
    <>
      <span>{label}</span>
      <Tooltip
        effect="light"
        placement="topLeft"
        content="票型为该项目总共获得的合计票数：通过票/总票数（通过票占比）"
      >
        <Icon name="question-o" className="info-render-th" />
      </Tooltip>
    </>
  );
};
const projectColumns: Column<any> = { prop: 'projectNo', label: '项目', align: 'center' };
const applyColumns: Column<any> = { prop: 'scientistName', label: '申报人姓名', align: 'center' };
const agencyColumns: Column<any> = { prop: 'organizationName', label: '申报人姓名', align: 'center' };
const judgeStatusColumns: Column<any> = {
  prop: 'judgeStatusName',
  label: '评审状态',
  align: 'center',
  render: (text, record) => {
    const { judgeStatus } = record;
    const status = judgeStatusMap.findIndex((item) => item === judgeStatus);
    return text ? (
      <span className={`status status-${Number(status)}`}>{text}</span>
    ) : '-';
  },
};
const scoreColumns: Column<any> = {
  prop: 'score', label: '得分', align: 'center', sortable: true,
};
const reversoContextColumns: Column<any> = {
  prop: 'vote', label: '票型', align: 'center', sortable: true, renderTh: infoRenderTh,
};
const currentReversoContextColumns: Column<any> = {
  prop: 'vote', label: '本轮票型', align: 'center', sortable: true, renderTh: infoRenderTh,
};

// 青山
// 待确认【项目、申报人姓名、操作】；
// 已确认+仅评审【项目、申报人姓名、评审状态、得分、操作】
// 已确认+评审和投票【项目、申报人姓名、评审状态、得分、本轮票型、操作】
// 已确认+仅投票【项目、申报人姓名、评审状态、票型】
// 未到轮次，无法操作，操作栏消失
// 科创
// 待确认【项目、机构名称、操作】；
// 已确认+仅评审【项目、机构名称、评审状态、得分、操作】
// 已确认+评审和投票【项目、机构名称、评审状态、得分、本轮票型、操作】
// 已确认+仅投票【项目、机构名称、评审状态、票型】
// 未到轮次，无法操作，操作栏消失
const getStaticColumns = (hasConfirmed, needReview, needVote, isGreen) => {
  const nameColumns = isGreen ? applyColumns : agencyColumns;
  if (!hasConfirmed) {
    return [projectColumns, nameColumns];
  } else if (needReview && needVote) {
    return [projectColumns, nameColumns, judgeStatusColumns, scoreColumns];
  } else if (needReview && !needVote) {
    return [projectColumns, nameColumns, judgeStatusColumns, scoreColumns];
  } else if (!needReview && needVote) {
    return [projectColumns, nameColumns, judgeStatusColumns];
  } else {
    return [];
  }
};

const ReviewTable = function ({ round, hasConfirm, projectType }) {
  const {
    needReview, needVote, VoteStatus, id: roundId, projects,
  } = round;
  const history = useHistory();
  const query = useQuery();
  const groupId = query.get('groupId');
  const staticColumns: Column<any>[] = getStaticColumns(hasConfirm, needReview, needVote, projectType === 1);
  const columns: Column<any>[] = staticColumns.concat([
    {
      prop: 'id',
      label: '操作',
      align: 'center',
      width: 380,
      render: (_, item) => {
        const { id, judgeStatus, projectNo } = item;
        const hasReviewed = judgeStatus;
        const detailPath = `/review/detail/${projectNo}?projectType=${projectType}&reviewId=${id}&groupId=${groupId}&roundId=${roundId}`;
        if (!hasConfirm) {
          return (
            <Button
              type="brand-text"
              style={{ color: '#FF6A00' }}
              onClick={() => {
                history.push(detailPath);
              }}
            >
              预览项目详情
            </Button>
          );
        } else if (hasReviewed) {
          return (
            <Button
              type="brand-text"
              style={{ color: '#FF6A00' }}
              onClick={() => {
                history.push(detailPath);
              }}
            >
              查看评审详情
            </Button>
          );
        } else {
          return (
            <Button
              type="brand-text"
              style={{ color: '#FF6A00' }}
              onClick={() => {
                history.push(detailPath);
              }}
            >
              点击此处评审
            </Button>
          );
        }
      },
    },
  ]);
  // 必须要 sorter，只设置 sortable 为 true 不生效
  const sorter = (a: any, b: any, sort: any) => {
    const { prop, order } = sort;
    if (!prop || !order || prop === 'name') return -1; // 忽略姓名排序
    const d = order === 'asc' ? 1 : -1;
    return a[prop] > b[prop] ? d : -d;
  };
  return (
    <Table
      sorter={sorter}
      className="review-list__group__content"
      // showIndex
      border
      rowKey="projectNo"
      columns={columns}
      data={projects}
    />
  );
};

const ReviewFoot = ({
  groupId, id: roundId, voteStatus, action, groupName, projectType, needReview, needVote, projects,
}) => {
  // const voteAvailable = voteStatus === VoteStatus.AVAILABLE;
  // const voteDone = voteStatus === VoteStatus.DONE;
  const voteNotStart = voteStatus === VoteStatus.DISABLED;
  const [modelVisible, setVisible] = useState(false);
  const [voteAvailable, setVoteAvailable] = useState(voteStatus === VoteStatus.AVAILABLE);
  const [voteDone, setVoteDone] = useState(voteStatus === VoteStatus.DONE);
  return (
    !voteNotStart ? (
      <div className="review-list__group__foot">
        {voteAvailable && '您已完成组内项目的评审结论提交，请投票。'}
        <Button onClick={() => setVisible(!modelVisible)}>
          {voteDone ? '查看投票' : '去投票'}
        </Button>
        <VoteModal
          roundId={roundId}
          projectType={projectType}
          groupId={groupId}
          groupName={groupName}
          voteAvailable={voteAvailable}
          setVoteAvailable={(v) => { setVoteAvailable(v); }}
          setVoteDone={(v) => { setVoteDone(v); }}
          visible={modelVisible}
          onVisibleChange={setVisible}
          reloadList={action}
          needReview={needReview}
          needVote={needVote}
        />
      </div>
    ) : !needVote ? null : (
      <div className="review-list__group__foot">
        投票通道尚未开启，您需要提交全部评审结果之后才可开启投票通道。
        <Button onClick={() => {
          batchSubmit({ groupId, roundId })
            .then((res) => {
              location.reload();
            }, (_errMsg) => {
              let errMsg = _errMsg;
              projects.forEach((item) => {
                const { projectNo, scientistName, organizationName } = item;
                const applyName = scientistName || organizationName;
                errMsg = errMsg.replace(projectNo, applyName);
              });
              Toast.open({
                title: errMsg, position: 'topCenter', duration: 1000, status: 'fail',
              });
            });
        }}
        >
          全部提交
        </Button>
      </div>
    )
  );
};

const ReviewList = function ({ data }) {
  // todo 当前阶段字段还没有给出
  const history = useHistory();
  const query = useQuery();
  const groupId = query.get('groupId');
  const {
    groupName, projectConfigName, projectStageName, groupStatus, rounds, projectType,
  } = data;
  return (
    <div className="review-list" key={groupId}>
      <div className="review-operation-head">
        <p className="title">
          {groupName}
          组项目列表
        </p>
        <Button
          className="review-list-back-button" type="hollow" size="small" onClick={() => {
            history.push('/review/groupList');
          }}
        >
          返回组别列表
        </Button>
      </div>
      <p className="review-list__group__head_v2">
        {projectConfigName}
        {' '}
        | 阶段：
        {projectStageName}
        {' '}
        | 组别状态：
        {groupStatus}
      </p>
      <div>
        {rounds.map((round, index) => {
          const { needReview, needVote, id } = round;
          const hasConfirm = groupStatus === '已确认';
          return (
            <div key={id} className="review-list__group">
              <p className="review-list__group__head">
                第
                {numberMap[index]}
                轮次：
                {getJudgeApproach(needReview, needVote)}
              </p>
              <ReviewTable
                round={round}
                hasConfirm={hasConfirm}
                projectType={projectType}
              />
              <ReviewFoot {...round} {...data} action={Function.prototype} groupId={groupId} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default () => {
  const query = useQuery();
  const groupId = query.get('groupId');
  const [projects, setProjects] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getJudgeProjects({ groupId })
      .then((res) => {
        setProjects(res);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <Header plate="评审平台" />
      <div className="page-list" style={{ marginTop: 0 }}>
        <div className="warp">
          {projects
            && (projects.total === 0 ? (
              <Empty />
            ) : loading ? (
              <Loading visible backdrop fullscreen />
            ) : projects.groups.map((item, index) => (<ReviewList data={item} key={index} />)))}
        </div>
      </div>
    </div>
  );
};
